import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUserStore } from '../stores/StoreProvider';
import OrganizationAutocomplete from '../components/OrganizationAutocomplete';

const OnboardingTeamOrganization = observer(() => {
  const navigate = useNavigate();
  const userStore = useUserStore();
  const [age, setAge] = useState('');
  const [level, setLevel] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ageLevels = [
    '6U', '7U', '8U', '9U', '10U', '11U', '12U', 
    '13U', '14U', '15U', '16U', '17U', '18U'
  ];

  const levels = [
    'House', 'B', 'A', 'AA', 'AAA', 'Junior', 'College', 'Pro'
  ];

  const handleFinish = async () => {
    if (!age.trim()) {
      toast.error('Please select your current age level.');
      return;
    }
    if (!level.trim()) {
      toast.error('Please select your current level.');
      return;
    }
    if (!selectedOrganization) {
      toast.error('Please select your organization.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Complete onboarding with all collected data
      await userStore.completeOnboarding({
        first_name: userStore.onboardingData?.firstName,
        last_name: userStore.onboardingData?.lastName,
        birthdate: userStore.onboardingData?.birthdate,
        role: userStore.onboardingData?.role,
        position: userStore.onboardingData?.position,
        level: level.trim(),
        zipcode: userStore.onboardingData?.zipcode,
        age: age.trim(),
        organization_id: selectedOrganization?.id,
      });

      toast.success('Welcome to Biscuit!');
      navigate('/app/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete onboarding. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/birthday-zipcode');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-2xl">B</span>
              </div>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            Almost done!
          </h2>
          <p className="text-blue-100">
            Tell us about your current team and organization
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-6">
            {/* Organization Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization
              </label>
              <OrganizationAutocomplete
                onSelect={setSelectedOrganization}
                placeholder="Search for your organization..."
              />
              {selectedOrganization && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {selectedOrganization.image_url && (
                      <img
                        src={selectedOrganization.image_url}
                        alt="Organization"
                        className="w-6 h-6 rounded object-contain"
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Selected: {selectedOrganization.name}
                      </p>
                      {selectedOrganization.team_name && (
                        <p className="text-xs text-blue-700">
                          {selectedOrganization.team_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Age Level Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Age Level
              </label>
              <select
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select your current age level</option>
                {ageLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            {/* Level Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Level
              </label>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select your current level</option>
                {levels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleBack}
                className="flex-1 flex justify-center items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="mr-2" size={20} />
                Back
              </button>
              
              <button
                onClick={handleFinish}
                disabled={!age.trim() || !level.trim() || !selectedOrganization || isSubmitting}
                className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    Finish
                    <ArrowRight className="ml-2" size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white/30 rounded-full"></div>
            <div className="w-3 h-3 bg-white rounded-full"></div>
          </div>
          <p className="text-white/80 text-sm mt-2">Step 5 of 5</p>
        </div>
      </div>
    </div>
  );
});

export default OnboardingTeamOrganization; 